import { gql } from '@apollo/client';
import { useState } from 'react';

import { ProgressButton } from 'play/molecules/track-progress/ProgressButton';
import { ProgressModalContainer } from 'play/organisms/progress-modal/ProgressModalContainer';
import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';
import { ChallengeStatus, Mode, ProgressChallengeFragment, ProgressTrackFragment, TrackStatus } from 'types/graphql';

gql`
  fragment ProgressChallenge on Challenge {
    id
    status
    ...ProgressModalChallenge
  }
  fragment ProgressTrack on Track {
    ...ProgressModalTrack
  }
`;

export interface ProgressProps {
  className?: string;
  teamSlug: string;
  challenges: ProgressChallengeFragment[];
  currentChallenge?: ProgressChallengeFragment;
  mode: Mode;
  skippingEnabled: boolean;
  track: ProgressTrackFragment;
  trackReviewsEnabled?: boolean;
  challengeTimer?: React.ReactNode;
}

export const Progress = ({
  className,
  teamSlug,
  challenges,
  currentChallenge,
  mode,
  track,
  skippingEnabled,
  trackReviewsEnabled,
  challengeTimer,
}: ProgressProps) => {
  const [modalOpen, setModalOpen] = useState(false);

  const challengeCount = challenges.length;
  const challengesCompleted = challenges.filter(c => c.status === ChallengeStatus.Completed).length;
  const currentChallengeIndex = currentChallenge && challenges.findIndex(c => c.id === currentChallenge.id);

  const trackAmplitudeEvent = useTrackAmplitudeEvent();

  const handleModalOpen = () => {
    trackAmplitudeEvent({ name: 'Lab - Clicked Track Outline' });
    setModalOpen(true);
  };

  const trackReady = track.status ? [TrackStatus.Created, TrackStatus.Started].includes(track.status) : false;
  const challengeReady =
    !currentChallenge ||
    [ChallengeStatus.Created, ChallengeStatus.Started, ChallengeStatus.Completed].includes(currentChallenge.status);
  const loading = !trackReady || !challengeReady;

  return (
    <>
      <ProgressButton
        className={className}
        challengeCount={challengeCount}
        challengesCompleted={challengesCompleted}
        currentChallengeIndex={currentChallengeIndex}
        onClick={handleModalOpen}
        challengeTimer={challengeTimer}
      />

      <ProgressModalContainer
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={mode}
        teamSlug={teamSlug}
        track={track}
        challenges={challenges}
        skippingEnabled={skippingEnabled}
        currentChallenge={currentChallenge}
        loading={loading}
        trackReviewsEnabled={trackReviewsEnabled}
      />
    </>
  );
};
